// src/pages/RareCancerDetailPage.jsx
// Authoritative Clinical Guide & Specialist Directory for Rare & Ultra-Rare Cancers.

import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  ShieldAlert,
  AlertTriangle,
  Microscope, 
  Dna, 
  Activity, 
  Building2, 
  UserCheck, 
  ArrowRight, 
  ArrowLeft,
  ChevronDown, 
  HelpCircle, 
  CheckCircle2, 
  ExternalLink,
  Phone,
  Sparkles,
  Calendar,
  Layers,
  HeartPulse
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { SITE_ORIGIN } from '@/config/site';
import { RARE_CANCERS_DATA, RARE_CANCERS_BY_SLUG } from '@/data/rareCancersData';
import Footer from '@/components/Footer';

export default function RareCancerDetailPage() {
  const { cancerSlug } = useParams();
  const [openFaq, setOpenFaq] = useState(null);

  const cancer = RARE_CANCERS_BY_SLUG[cancerSlug];

  if (!cancer) {
    return <Navigate to="/rare-cancers" replace />;
  }

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const pageUrl = `${SITE_ORIGIN}${cancer.path}`;
  const hubUrl = `${SITE_ORIGIN}/rare-cancers`;

  // Filter 4 related rare cancers in same or adjacent category
  const relatedCancers = RARE_CANCERS_DATA
    .filter((c) => c.slug !== cancer.slug)
    .sort((a, b) => (a.category === cancer.category ? -1 : 1))
    .slice(0, 4);

  // Schema.org Structured Data
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: cancer.meta.title,
      description: cancer.meta.description,
      url: pageUrl,
      lastReviewed: cancer.lastReviewed,
      medicalAudience: {
        '@type': 'MedicalAudience',
        audienceType: 'Patient',
      },
      about: {
        '@type': 'MedicalCondition',
        name: cancer.name,
        alternateName: cancer.shortName,
        possibleTreatment: cancer.treatmentOptions.map((t) => ({
          '@type': 'MedicalTherapy',
          name: t,
        })),
        signOrSymptom: cancer.symptoms.map((s) => ({
          '@type': 'MedicalSignOrSymptom',
          name: s,
        })),
      },
      provider: {
        '@type': 'MedicalOrganization',
        name: 'ByOnco Rare Cancer Tumor Board & Navigation',
        url: SITE_ORIGIN,
        logo: `${SITE_ORIGIN}/byonco-logo.png`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Rare Cancers Directory', item: hubUrl },
        { '@type': 'ListItem', position: 3, name: cancer.name, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: cancer.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Vetted Specialists for ${cancer.name}`,
      itemListElement: cancer.specialists.map((s, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'Physician',
          name: s.name,
          jobTitle: s.title,
          worksFor: {
            '@type': 'MedicalOrganization',
            name: s.institution,
            address: {
              '@type': 'PostalAddress',
              addressLocality: s.city,
              addressCountry: s.country,
            },
          },
        },
      })),
    },
  ];

  const isEmergent = cancer.reviewScope === 'RED_FLAGS_ONLY' || (cancer.redFlags && cancer.redFlags.length > 0);

  return (
    <div className="min-h-screen bg-cream-paper text-charcoal font-sans antialiased">
      <MetaTags
        title={cancer.meta.title}
        description={cancer.meta.description}
        path={cancer.path}
        jsonLd={jsonLd}
      />

      {/* Top Sticky Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="border-b border-border-mist bg-white/70 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between text-xs sm:text-sm">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-forest-ink transition">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/rare-cancers" className="text-gray-500 hover:text-forest-ink transition">
                Rare Cancers Directory
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-forest-ink font-semibold truncate max-w-[200px] sm:max-w-xs" aria-current="page">
              {cancer.shortName}
            </li>
          </ol>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/second-opinion"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-ink text-white text-xs font-medium hover:bg-forest-shadow transition"
            >
              Get Second Opinion <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <header className="pt-10 pb-14 bg-gradient-to-b from-emerald-50/60 via-cream-paper to-cream-paper border-b border-border-mist">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-mist text-forest-ink text-xs font-semibold border border-hair uppercase tracking-wider">
              <Activity className="w-3.5 h-3.5 text-forest-ink" />
              {cancer.category.toUpperCase()} CANCER
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/80 text-charcoal text-xs font-medium border border-border-mist">
              {cancer.type}
            </span>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-medium border border-emerald-200">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              WHO 5th Edition Classification
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-normal text-forest-ink tracking-tight mb-4 leading-tight font-serif">
            {cancer.name}
          </h1>

          <p className="text-base sm:text-lg text-emerald-900/90 font-medium mb-6">
            {cancer.categoryLabel} &bull; Clinical Staging, Genomic Targets & Vetted Specialists
          </p>

          <p className="text-base sm:text-lg text-charcoal/90 leading-relaxed mb-8 max-w-4xl">
            {cancer.description}
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/second-opinion"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white font-semibold text-sm sm:text-base transition shadow-sm hover:shadow-md"
            >
              Request Rare Cancer Tumor Board <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/find-cancer-centers"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white border border-border-mist hover:border-forest-ink text-forest-ink font-semibold text-sm sm:text-base transition shadow-sm"
            >
              <Building2 className="w-4 h-4 text-emerald-700" />
              Find NCI Treatment Centers
            </Link>
            <Link
              to="/talk"
              className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-mist hover:bg-mist/80 text-forest-ink font-medium text-sm transition"
            >
              <Sparkles className="w-4 h-4 text-amber-600" />
              Ask ByOnco AI Navigator
            </Link>
          </div>
        </div>
      </header>

      {/* Emergent Red Flags Alert (if applicable) */}
      {isEmergent && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 mb-10">
          <div className="rounded-2xl border-2 border-red-300 bg-red-50/90 p-5 sm:p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="p-2.5 rounded-xl bg-red-100 text-red-700 shrink-0 mt-0.5">
                <ShieldAlert className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-red-950 mb-1 flex items-center gap-2">
                  <span>Emergent Referral Advisory</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-200 text-red-900 uppercase font-semibold">
                    Immediate Action
                  </span>
                </h2>
                <p className="text-sm text-red-900/90 mb-3 leading-relaxed">
                  Certain rare malignancies progress rapidly or carry acute risk of airway obstruction, acute spinal compression, hydrocephalus, or biopsy-seeding. Do not perform needle biopsy or surgery outside of an NCI-designated specialty sarcoma or neuro-oncology unit without multidisciplinary tumor board review.
                </p>
                {cancer.redFlags && cancer.redFlags.length > 0 && (
                  <div className="mt-3 bg-white/90 rounded-xl p-4 border border-red-200">
                    <p className="text-xs font-bold text-red-900 uppercase tracking-wider mb-2">
                      Report immediately to an emergency department or tertiary oncologist upon:
                    </p>
                    <ul className="grid sm:grid-cols-2 gap-2 text-xs sm:text-sm text-red-950">
                      {cancer.redFlags.map((flag, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                          <span>{flag}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content Body */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">

        {/* Key Clinical Epidemiology & Demographics */}
        <section className="grid sm:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-hair shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800 uppercase tracking-wider mb-2">
              <UserCheck className="w-4 h-4 text-emerald-700" />
              Who It Affects
            </div>
            <p className="text-sm sm:text-base text-forest-ink font-medium leading-relaxed">
              {cancer.whoItAffects || 'Pediatric, adolescent, and adult populations depending on histologic subtype.'}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-hair shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800 uppercase tracking-wider mb-2">
              <Activity className="w-4 h-4 text-emerald-700" />
              Annual Incidence
            </div>
            <p className="text-sm sm:text-base text-forest-ink font-medium leading-relaxed">
              {cancer.incidence || 'Estimated under 1 to 5 cases per 1,000,000 population annually.'}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-hair shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800 uppercase tracking-wider mb-2">
              <Calendar className="w-4 h-4 text-emerald-700" />
              Clinical Evidence Review
            </div>
            <p className="text-sm text-forest-ink leading-relaxed">
              Last audited: <span className="font-semibold">{cancer.lastReviewed}</span> against NCCN, ESMO, and WHO 5th ed. guidelines.
            </p>
          </div>
        </section>

        {/* Symptoms & Presentation */}
        <section className="bg-white rounded-2xl p-6 sm:p-8 border border-hair shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-xl bg-mist text-forest-ink">
              <HeartPulse className="w-5 h-5 text-forest-ink" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-forest-ink font-serif">
                Clinical Presentation & Hallmark Symptoms
              </h2>
              <p className="text-xs sm:text-sm text-charcoal/80">
                Presenting signs most frequently observed across clinical case series
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {cancer.symptoms.map((symptom, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3.5 rounded-xl bg-cream-paper/70 border border-border-mist">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span className="text-sm text-charcoal font-medium leading-snug">{symptom}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Molecular Markers & Genomic Testing */}
        {cancer.molecularMarkers && cancer.molecularMarkers.length > 0 && (
          <section className="bg-gradient-to-br from-white to-emerald-50/40 rounded-2xl p-6 sm:p-8 border border-hair shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-xl bg-emerald-100 text-emerald-800">
                <Dna className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-forest-ink font-serif">
                  Genomic Profiling & Defining Molecular Lesions
                </h2>
                <p className="text-xs sm:text-sm text-charcoal/80">
                  Critical diagnostic fusions, somatic mutations, and therapeutic targets
                </p>
              </div>
            </div>

            <p className="text-sm sm:text-base text-charcoal mb-6 leading-relaxed">
              Rare malignancies frequently depend on distinct oncogenic drivers rather than conventional environmental carcinogens. Comprehensive Next-Generation Sequencing (NGS comprehensive panel) and FISH/IHC are mandatory to establish the true diagnosis and screen for basket trial agents.
            </p>

            <div className="flex flex-wrap gap-2.5">
              {cancer.molecularMarkers.map((marker, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white border border-emerald-200 text-emerald-950 font-mono text-xs sm:text-sm shadow-xs font-semibold"
                >
                  <Dna className="w-3.5 h-3.5 text-emerald-600" />
                  {marker}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Treatment Modalities & Standard of Care */}
        <section className="bg-white rounded-2xl p-6 sm:p-8 border border-hair shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-xl bg-mist text-forest-ink">
              <Microscope className="w-5 h-5 text-forest-ink" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-forest-ink font-serif">
                Standard-of-Care Treatment Protocol
              </h2>
              <p className="text-xs sm:text-sm text-charcoal/80">
                Frontline and multimodal strategies established under international consensus guidelines
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            {cancer.treatmentOptions.map((treatment, idx) => (
              <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-mist/60 border border-hair">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-forest-ink text-white text-xs font-bold shrink-0 mt-0.5">
                  {idx + 1}
                </span>
                <div>
                  <h3 className="text-base font-semibold text-forest-ink mb-1">
                    {treatment}
                  </h3>
                  <p className="text-xs sm:text-sm text-charcoal/80 leading-relaxed">
                    Administered in specialized high-volume oncology programs with subspecialty pathology and organ-preservation protocols.
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 rounded-xl bg-emerald-50/70 border border-emerald-200 text-xs sm:text-sm text-emerald-900 leading-relaxed">
            <strong>Clinical Trial Advisory:</strong> For rare and ultra-rare malignancies, enrollment in an active clinical trial or expanded-access program is widely considered the preferred standard of care by ASCO and NCCN panels.
          </div>
        </section>

        {/* Prognosis & Clinical Outlook */}
        <section className="bg-white rounded-2xl p-6 sm:p-8 border border-hair shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-xl bg-mist text-forest-ink">
              <Activity className="w-5 h-5 text-forest-ink" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-forest-ink font-serif">
                Prognosis & Disease Trajectory
              </h2>
              <p className="text-xs sm:text-sm text-charcoal/80">
                Objective clinical outlook without false reassurance
              </p>
            </div>
          </div>

          <div className="rounded-xl bg-cream-paper/80 p-5 border border-border-mist text-sm sm:text-base text-forest-ink leading-relaxed">
            {cancer.prognosis}
          </div>

          <p className="text-xs text-charcoal/70 mt-3 italic">
            Note: Statistics reflect cohort averages. Individual outcomes depend heavily on performance status, resectability, biomarker expression, and timely access to specialized tertiary care.
          </p>
        </section>

        {/* Active Research & Clinical Trials */}
        <section className="bg-white rounded-2xl p-6 sm:p-8 border border-hair shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-xl bg-amber-100 text-amber-800">
              <Sparkles className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-forest-ink font-serif">
                Active Research, Biomarkers & Clinical Trials
              </h2>
              <p className="text-xs sm:text-sm text-charcoal/80">
                Novel investigational agents, phase I/II trials, and international rare disease consortia
              </p>
            </div>
          </div>

          <p className="text-sm sm:text-base text-charcoal mb-6 leading-relaxed">
            {cancer.researchStatus}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-5 rounded-xl bg-emerald-50 border border-emerald-200">
            <div>
              <h4 className="text-sm font-bold text-emerald-950 mb-1">
                Need Help Matching to an Active Rare Cancer Trial?
              </h4>
              <p className="text-xs text-emerald-800 leading-relaxed">
                ByOnco scans ClinicalTrials.gov, NCI trial networks, and institutional registries for open patient cohorts.
              </p>
            </div>
            <Link
              to="/second-opinion"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-forest-ink text-white text-xs font-semibold hover:bg-forest-shadow shrink-0 transition"
            >
              Request Trial Match <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </section>

        {/* Verified Specialists Directory */}
        <section className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2">
            <div>
              <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wider block mb-1">
                Expert Clinicians
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-forest-ink font-serif">
                Verified Specialists for {cancer.shortName}
              </h2>
            </div>
            <Link
              to="/find-oncologists"
              className="text-xs sm:text-sm font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
            >
              Browse All Oncologists <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {cancer.specialists.map((spec, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 border border-hair shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="text-lg font-bold text-forest-ink">
                        {spec.name}
                      </h3>
                      <p className="text-xs font-medium text-emerald-800">
                        {spec.title}
                      </p>
                    </div>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-mist text-forest-ink border border-hair shrink-0">
                      {spec.experienceYears}+ yrs exp
                    </span>
                  </div>

                  <p className="text-sm font-medium text-charcoal/90 mb-1">
                    {spec.institution}
                  </p>
                  <p className="text-xs text-charcoal/70 mb-4">
                    {[spec.city, spec.country].filter(Boolean).join(', ')}
                  </p>

                  <div className="flex flex-wrap gap-1.5 mb-6">
                    {spec.specialties.map((sp, sIdx) => (
                      <span key={sIdx} className="text-[11px] px-2 py-0.5 rounded-md bg-cream-paper text-forest-ink border border-border-mist">
                        {sp}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-border-mist flex items-center justify-between gap-2">
                  <span className="text-xs text-charcoal/80 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Vetted Directory
                  </span>
                  <Link
                    to="/teleconsultation"
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-forest-ink text-white text-xs font-semibold hover:bg-forest-shadow transition"
                  >
                    Consult Specialist <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Recommended NCI Comprehensive Cancer Centers */}
        <section className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2">
            <div>
              <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wider block mb-1">
                Hospital Network
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-forest-ink font-serif">
                Designated Cancer Centers with Dedicated Programs
              </h2>
            </div>
            <Link
              to="/find-cancer-centers"
              className="text-xs sm:text-sm font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
            >
              Search All 72 NCI Centers <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {cancer.recommendedCenters.map((center, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-5 border border-hair shadow-sm hover:border-forest-ink transition">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-base font-bold text-forest-ink">
                    <Link to={`/find-cancer-centers/${center.slug}`} className="hover:underline">
                      {center.name}
                    </Link>
                  </h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 font-medium shrink-0">
                    {center.state}
                  </span>
                </div>

                <p className="text-xs text-emerald-800 font-medium mb-2">
                  {center.designation}
                </p>

                <p className="text-xs text-charcoal/80 mb-3 leading-relaxed">
                  {center.relevance}
                </p>

                <div className="flex flex-wrap gap-1 mb-4">
                  {center.technologies.slice(0, 3).map((tech, tIdx) => (
                    <span key={tIdx} className="text-[10px] px-2 py-0.5 rounded bg-mist text-forest-ink">
                      {tech}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between text-xs pt-3 border-t border-border-mist">
                  <span className="text-charcoal/70">{center.trials}</span>
                  <Link
                    to={`/find-cancer-centers/${center.slug}`}
                    className="font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                  >
                    View Center Profile <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Frequently Asked Questions */}
        <section className="bg-white rounded-2xl p-6 sm:p-8 border border-hair shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-xl bg-mist text-forest-ink">
              <HelpCircle className="w-5 h-5 text-forest-ink" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-forest-ink font-serif">
                Frequently Asked Clinical Questions
              </h2>
              <p className="text-xs sm:text-sm text-charcoal/80">
                Authoritative guidance on diagnosis, tumor boards, genomic markers, and care options
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {cancer.faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className="rounded-xl border border-hair overflow-hidden transition"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full text-left p-4 sm:p-5 flex items-center justify-between gap-4 bg-cream-paper/50 hover:bg-cream-paper transition"
                  >
                    <span className="text-sm sm:text-base font-semibold text-forest-ink">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-forest-ink shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="p-4 sm:p-5 bg-white border-t border-border-mist text-xs sm:text-sm text-charcoal leading-relaxed">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Related Rare Cancers */}
        <section className="space-y-4">
          <h3 className="text-lg font-bold text-forest-ink font-serif">
            Related Rare Malignancies in Catalog
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            {relatedCancers.map((rel) => (
              <Link
                key={rel.slug}
                to={rel.path}
                className="p-4 rounded-xl bg-white border border-hair hover:border-forest-ink transition group"
              >
                <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded bg-mist text-forest-ink block w-fit mb-1">
                  {rel.category}
                </span>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink group-hover:underline leading-snug">
                  {rel.name}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* Conversion & Multidisciplinary Review CTA Card */}
        <section className="rounded-3xl bg-forest-ink text-white p-8 sm:p-12 text-center relative overflow-hidden shadow-xl">
          <div className="max-w-3xl mx-auto space-y-6 relative z-10">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-white/10 text-emerald-200 text-xs font-semibold uppercase tracking-wider">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              Independent Tumor Board Review
            </span>

            <h2 className="text-2xl sm:text-4xl font-normal font-serif text-white tracking-tight leading-tight">
              Facing a Diagnosis of {cancer.shortName}?
            </h2>

            <p className="text-sm sm:text-base text-cream-paper/90 leading-relaxed max-w-2xl mx-auto">
              Rare cancers demand rare expertise. Connect your medical records to US NCI-fellowship trained oncologists, subspecialist pathologists, and active clinical trial investigators within 12 to 72 hours.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Link
                to="/second-opinion"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white text-forest-ink hover:bg-cream-paper font-semibold text-sm sm:text-base transition shadow-md hover:shadow-lg"
              >
                Upload Scans for Second Opinion <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/teleconsultation"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-emerald-900/60 hover:bg-emerald-900/80 text-white border border-emerald-700/50 font-semibold text-sm sm:text-base transition"
              >
                Schedule Virtual Teleconsult
              </Link>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
